/* eslint-disable react/destructuring-assignment */
import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { fade, withStyles, createStyles } from "@material-ui/core/styles";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem, { TreeItemProps } from "@material-ui/lab/TreeItem";
import Collapse from "@material-ui/core/Collapse";
import { useSpring, animated } from "react-spring/web.cjs";
import { TransitionProps } from "@material-ui/core/transitions";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { tesaGrey3 } from "../../../styles/colors";

export interface Tree<T> {
  id: number;
  name: string;
  data: T;
  children: Tree<T>[];
}

// Helper Components
const TransitionComponent = (props: TransitionProps): ReactElement => {
  const style = useSpring({
    from: { opacity: 0, transform: "translate3d(20px,0,0)" },
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
};

const StyledTreeItem = withStyles(() =>
  createStyles({
    iconContainer: {
      "& .close": {
        opacity: 0.3,
      },
    },
    group: {
      marginLeft: 8,
      paddingLeft: 18,
      borderLeft: `1px solid ${fade(tesaGrey3, 0.4)}`,
    },
  }),
)((props: TreeItemProps) => <TreeItem {...props} TransitionComponent={TransitionComponent} />);

interface CustomTreeViewProps<T> {
  nodes: Tree<T>[];
  onNodeSelect: (node: T) => void;
  highlighted?: string;
}

function CustomTreeView<T>(props: CustomTreeViewProps<T>): JSX.Element {
  const { nodes, onNodeSelect, highlighted } = props;

  const extractData = (treeNodes: Tree<T>[]): { [key: string]: T } => {
    const dataMap: { [key: string]: T } = {};
    treeNodes.forEach((treeNode) => {
      dataMap[`${treeNode.id}`] = treeNode.data;
      const childrenData = extractData(treeNode.children);
      Object.keys(childrenData).forEach((key) => {
        dataMap[key] = childrenData[key];
      });
    });
    return dataMap;
  };

  const dataMap = extractData(nodes);

  const checkDefaultExpanded = (treeNodes: Tree<T>[]): string[] => {
    const expandedIds: string[] = [];
    treeNodes.forEach((treeNode) => {
      const add = treeNode.children
        .map((childNode) => highlighted && childNode.name.toLowerCase().includes(highlighted.toLowerCase()))
        .reduce((a, b) => a || b, false);
      if (add) {
        expandedIds.push(`${treeNode.id}`);
      }
      const expandedChildrenIds = checkDefaultExpanded(treeNode.children);
      if (expandedChildrenIds.length > 0 && !expandedIds.includes(`${treeNode.id}`)) {
        expandedIds.push(`${treeNode.id}`);
      }
      expandedChildrenIds.forEach((expandedChildrenId) => {
        expandedIds.push(expandedChildrenId);
      });
    });
    return expandedIds;
  };

  const [expanded, setExpanded] = useState<string[]>([]);
  useEffect(() => {
    setExpanded(checkDefaultExpanded(nodes));
  }, [highlighted]);

  const renderHighlightedLabel = (label: string): JSX.Element[] | string => {
    if (!highlighted) {
      return label;
    }
    const searched = label.toLowerCase();
    const matches: number[] = [];
    let current = searched.indexOf(highlighted.toLowerCase());
    while (current >= 0) {
      matches.push(current);
      current = searched.indexOf(highlighted.toLowerCase(), current + 1);
    }
    let lastIndex = 0;
    const highlightedLabel: JSX.Element[] = [];
    matches.forEach((match) => {
      highlightedLabel.push(<span>{label.substring(lastIndex, match)}</span>);
      lastIndex = match + highlighted.length;
      highlightedLabel.push(<b>{label.substring(match, lastIndex)}</b>);
    });
    highlightedLabel.push(<span>{label.substring(lastIndex, label.length)}</span>);
    return highlightedLabel;
  };

  const renderTree = (dataObj: Tree<T>): JSX.Element => (
    <StyledTreeItem key={dataObj.id} nodeId={`${dataObj.id}`} label={renderHighlightedLabel(dataObj.name)}>
      {Array.isArray(dataObj.children) ? dataObj.children.map((node) => renderTree(node)) : null}
    </StyledTreeItem>
  );

  if (nodes.length === 0) {
    return <em>No Data</em>;
  }

  const handleChange = (event: React.ChangeEvent<{}>, nodeIds: string[]): void => {
    setExpanded(nodeIds);
  };

  return (
    <TreeView
      onNodeSelect={(event: ChangeEvent<unknown>, nodeIds: string): void => {
        onNodeSelect(dataMap[nodeIds]);
      }}
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      expanded={expanded}
      onNodeToggle={handleChange}
    >
      {nodes.map((node: Tree<T>) => renderTree(node))}
    </TreeView>
  );
}

export default CustomTreeView;
